import React, { useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import type { QnAFormData } from 'src/app/(default)/support/qna/write/_components';
import { cn } from 'src/app/lib/utils/tailwindcss';
import NextImage from 'src/components/_common/NextImage';

interface FileUploadAreaProps {
  maxFiles?: number;
  maxFileSize?: number; // MB
  acceptedTypes?: string[];
  onFilesChange?: (files: File[]) => void;
}

const FileUploadSection: React.FC<FileUploadAreaProps> = ({
  maxFiles = 3,
  maxFileSize = 5,
  acceptedTypes = ['image/jpeg', 'image/png', 'image/jpg'],
  onFilesChange,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { watch, setValue } = useFormContext<QnAFormData>();

  const files = watch('bbsFile') || [];

  const [error, setError] = useState<string>('');

  const validateFile = (file: File): boolean => {
    // 파일 크기 검증
    if (file.size > maxFileSize * 1024 * 1024) {
      setError(`파일 크기는 ${maxFileSize}MB 이하여야 합니다.`);
      return false;
    }

    // 파일 타입 검증
    const isValidType = acceptedTypes.some((type) => {
      return file.type === type;
    });

    if (!isValidType) {
      setError('지원되지 않는 파일 형식입니다.');
      return false;
    }

    return true;
  };

  const handleFiles = (newFiles: FileList | null) => {
    if (!newFiles) return;

    setError('');
    const fileArray = Array.from(newFiles);
    const validFiles: File[] = [];

    for (const file of fileArray) {
      if (files.length + validFiles.length >= maxFiles) {
        setError(`최대 ${maxFiles}개의 파일만 업로드할 수 있습니다.`);
        break;
      }

      if (validateFile(file)) {
        validFiles.push(file);
      } else {
        break;
      }
    }

    if (validFiles.length > 0) {
      const updatedFiles = [...files, ...validFiles];
      setValue('bbsFile', updatedFiles);
      onFilesChange?.(updatedFiles);
    }
  };

  const removeFile = (index: number) => {
    const updatedFiles = files.filter((_, i) => i !== index);
    setValue('bbsFile', updatedFiles);
    onFilesChange?.(updatedFiles);
    setError('');
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFiles(e.target.files);

    // 파일 입력 초기화
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const openFileDialog = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="w-full">
      <p className="pb-2 typo-body-2 text-text-caption-label">이미지 첨부</p>

      <div className="flex flex-wrap gap-2">
        {/* 업로드 영역 - 최대 파일 수에 도달하지 않았을 때만 표시 */}
        {files.length < maxFiles && (
          <div
            className={cn(
              'flex h-[100px] w-[100px] cursor-pointer items-center justify-center rounded-lg bg-[#f0f0f0] transition-colors',
              'border border-transparent focus-within:border-k-50 hover:bg-[#e8e8e8]',
            )}
            onClick={openFileDialog}
          >
            <div className="flex flex-col items-center justify-center rounded-full bg-white p-1">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M8 3V13" stroke="#686868" strokeWidth="1.5" strokeLinecap="round" />
                <path d="M13 8L3 8" stroke="#686868" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </div>

            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={acceptedTypes.join(',')}
              onChange={handleInputChange}
              className="hidden"
            />
          </div>
        )}

        {/* 업로드된 이미지들 미리보기 */}
        {files.map((file, index) => (
          <div key={`${file.name}-${index}`} className="relative">
            <div className="h-[100px] w-[100px] overflow-hidden rounded-lg bg-[#f0f0f0]">
              <NextImage
                src={URL.createObjectURL(file)}
                alt={file.name}
                width={100}
                height={100}
                className="h-full w-full object-cover"
              />
            </div>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                removeFile(index);
              }}
              className="absolute top-1 right-1 h-4 w-4 transition-opacity hover:opacity-80"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                <circle cx="8" cy="8" r="8" fill="#686868" />
                <path d="M4.5 4.5L11.5 11.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
                <path d="M4.5 11.5L11.5 4.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </button>
          </div>
        ))}
      </div>

      {/* Error Message */}
      {error && <p className="pt-2 typo-caption-1 text-text-error">{error}</p>}

      <p className="pt-2 typo-caption-1 text-[#b0b0b0]">
        이미지 첨부는 5MB 미만의 jpg,png 파일만 등록 가능하며, 최대 3개까지 등록 가능합니다.
        <br />
        개인정보 관련 피해 방지를 위해 이름, 연락처, 주소 등 개인정보가 노출되지 않도록 주의해주시기 바랍니다.
      </p>
    </div>
  );
};

export default FileUploadSection;
